import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useTheoreticalPassages } from '../hooks/useTheoreticalPassages'
import { useMultivertrekken } from '../hooks/useMultiVertrekken.hook'
import StateScreen, { StateType } from './StateScreen'
import Passages from './Passages'
import { determineState } from '../utils/states.utils'
import { ServerTimeContext } from '../contexts/serverTime.context'
import { useMessagesForStops } from '../hooks/useMessagesForStops.hook'
import Messages from './Messages'
import { hasLongMessage, getAmountOfMessageRows } from '../utils/message.utils'
import { IRTIS3_MAX_AMOUNT_OF_STOPS } from '../constants'

export default function Views() {
  const [searchParams] = useSearchParams()
  const params = useParams()
  const includeEindHaltes = searchParams.get('includeEindHaltes')
  const debugQueryParam = searchParams.get('debug')
  const stateQueryParam = searchParams.get('state')
  const previewQueryParam = searchParams.get('preview')
  const { getCalibratedDateTime } = React.useContext(ServerTimeContext)

  const { data: theoreticPassages, error: realtimeError } =
    useTheoreticalPassages()
  const {
    data: { passages: realtimePassages },
    error: theoreticError,
    isLoading: isLoadingPassages,
  } = useMultivertrekken({
    includeEindHaltes: includeEindHaltes !== 'false',
  })

  const determinedStateType = determineState({
    realtimePassages,
    theoreticPassages,
    realtimeError: !!realtimeError,
    theoreticError: !!theoreticError,
    tripIsNotServed:
      !!realtimePassages.length &&
      realtimePassages.every((trip) => trip.isNotServed),
    calibratedNow: getCalibratedDateTime(),
    queryParams: {
      debug: debugQueryParam === 'true',
      state: stateQueryParam,
    },
  })

  const isDebug = debugQueryParam === 'true' && !!stateQueryParam
  const stateType = isDebug
    ? (stateQueryParam as StateType)
    : determinedStateType

  const { messages, isLoading: isLoadingDisturbances } = useMessagesForStops({
    haltenummers: params.haltenummers as string,
    isPreview: previewQueryParam === 'true',
  })

  const loading = isLoadingPassages && isLoadingDisturbances
  const hasLongMessageThatNeeds2Rows = hasLongMessage(messages)
  const messagesRows = getAmountOfMessageRows(
    messages,
    hasLongMessageThatNeeds2Rows,
  )

  const amountParam = Number(params?.aantal)

  // Determine the number of passages to display
  const passagesAmount =
    !isNaN(amountParam) && amountParam > 0
      ? amountParam
      : IRTIS3_MAX_AMOUNT_OF_STOPS

  // Limit the number of passages to the maximum defined
  const passagesLimit = passagesAmount - messagesRows

  return (
    <section className="flex flex-col flex-grow bg-neutral-100 dark:bg-neutral-600">
      {!loading && stateType && <StateScreen type={stateType} />}
      {!loading && !stateType && (
        <>
          <Passages
            realtimePassages={realtimePassages}
            theoreticPassages={theoreticPassages}
            displayLimit={passagesLimit}
          />
          {messages.length > 0 && (
            <Messages
              messages={messages}
              needs2Rows={hasLongMessageThatNeeds2Rows}
            />
          )}
        </>
      )}
    </section>
  )
}
